import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft, Check, X, Eye, EyeOff } from 'lucide-react';
import AloriaLogo from '@/components/AloriaLogo';
import { formatApiErrorMessage } from '@/utils/errors';

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const token = searchParams.get('token');
  const [formData, setFormData] = useState({
    new_password: '',
    confirm_password: ''
  });

  const [validation, setValidation] = useState({
    minLength: false,
    hasUppercase: false,
    hasLowercase: false,
    hasNumber: false,
    match: false
  });

  useEffect(() => {
    // Vérifier que le token existe
    if (!token) {
      toast.error('Token manquant', {
        description: 'Veuillez cliquer sur le lien reçu par email'
      });
      navigate('/login');
    }
  }, [token, navigate]);

  useEffect(() => {
    // Valider le mot de passe en temps réel
    const password = formData.new_password;
    setValidation({
      minLength: password.length >= 8,
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasNumber: /[0-9]/.test(password),
      match: password.length > 0 && password === formData.confirm_password
    });
  }, [formData]);

  const allValid = Object.values(validation).every(v => v);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!allValid) {
      toast.error('Veuillez vérifier les critères du mot de passe');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          new_password: formData.new_password
        })
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('✅ Mot de passe réinitialisé avec succès!', {
          description: 'Vous pouvez maintenant vous connecter avec votre nouveau mot de passe'
        });
        
        // Rediriger vers login après 2 secondes
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      } else {
        toast.error(formatApiErrorMessage(data, 'Erreur lors de la réinitialisation'));
      }
    } catch (error) {
      toast.error('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0F172A] via-[#1E293B] to-[#0F172A] flex items-center justify-center p-4 sm:p-6 lg:p-8">
      <div className="w-full max-w-sm sm:max-w-md">
        <Button
          variant="ghost"
          onClick={() => navigate('/login')}
          className="mb-3 sm:mb-4 text-slate-300 hover:text-white hover:bg-slate-800 text-sm sm:text-base touch-manipulation"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour à la connexion
        </Button>

        <div className="text-center mb-6 sm:mb-8">
          <div className="flex items-center justify-center mb-3 sm:mb-4">
            <AloriaLogo className="h-12 sm:h-14" showText={true} />
          </div>
          <p className="text-slate-300 text-sm sm:text-base">Réinitialiser votre mot de passe</p>
        </div>

        <Card className="bg-gradient-to-br from-[#1E293B] to-[#334155] border-slate-700">
          <CardHeader className="pb-4 sm:pb-6">
            <CardTitle className="text-white text-lg sm:text-xl">Nouveau Mot de Passe</CardTitle>
            <CardDescription className="text-slate-400 text-sm sm:text-base">
              Créez un mot de passe fort et sécurisé
            </CardDescription>
          </CardHeader>

          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
              {/* Nouveau Mot de Passe */}
              <div>
                <Label htmlFor="password" className="text-slate-300 text-sm sm:text-base">
                  Nouveau Mot de Passe
                </Label>
                <div className="relative mt-1 sm:mt-2">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.new_password}
                    onChange={(e) => setFormData({ ...formData, new_password: e.target.value })}
                    placeholder="Entrez un mot de passe fort"
                    className="bg-[#0F172A] border-slate-600 text-white placeholder:text-slate-500 h-10 sm:h-11 pr-10 text-sm sm:text-base touch-manipulation"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Confirmation Mot de Passe */}
              <div>
                <Label htmlFor="confirm-password" className="text-slate-300 text-sm sm:text-base">
                  Confirmer le Mot de Passe
                </Label>
                <div className="relative mt-1 sm:mt-2">
                  <Input
                    id="confirm-password"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={formData.confirm_password}
                    onChange={(e) => setFormData({ ...formData, confirm_password: e.target.value })}
                    placeholder="Confirmez votre mot de passe"
                    className="bg-[#0F172A] border-slate-600 text-white placeholder:text-slate-500 h-10 sm:h-11 pr-10 text-sm sm:text-base touch-manipulation"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                  >
                    {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Critères de Validation */}
              <div className="bg-slate-900/50 border border-slate-700 rounded-lg p-4 space-y-2">
                <p className="text-slate-300 font-semibold text-sm">Critères obligatoires:</p>
                
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    {validation.minLength ? (
                      <Check className="w-4 h-4 text-green-500" />
                    ) : (
                      <X className="w-4 h-4 text-red-500" />
                    )}
                    <span className={`text-sm ${validation.minLength ? 'text-green-400' : 'text-slate-400'}`}>
                      Au moins 8 caractères
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    {validation.hasUppercase ? (
                      <Check className="w-4 h-4 text-green-500" />
                    ) : (
                      <X className="w-4 h-4 text-red-500" />
                    )}
                    <span className={`text-sm ${validation.hasUppercase ? 'text-green-400' : 'text-slate-400'}`}>
                      Au moins une majuscule
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    {validation.hasLowercase ? (
                      <Check className="w-4 h-4 text-green-500" />
                    ) : (
                      <X className="w-4 h-4 text-red-500" />
                    )}
                    <span className={`text-sm ${validation.hasLowercase ? 'text-green-400' : 'text-slate-400'}`}>
                      Au moins une minuscule
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    {validation.hasNumber ? (
                      <Check className="w-4 h-4 text-green-500" />
                    ) : (
                      <X className="w-4 h-4 text-red-500" />
                    )}
                    <span className={`text-sm ${validation.hasNumber ? 'text-green-400' : 'text-slate-400'}`}>
                      Au moins un chiffre
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    {validation.match && formData.confirm_password ? (
                      <Check className="w-4 h-4 text-green-500" />
                    ) : (
                      <X className="w-4 h-4 text-red-500" />
                    )}
                    <span className={`text-sm ${(validation.match && formData.confirm_password) ? 'text-green-400' : 'text-slate-400'}`}>
                      Les mots de passe correspondent
                    </span>
                  </div>
                </div>
              </div>

              {/* Bouton Soumettre */}
              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white shadow-lg shadow-orange-500/50 h-10 sm:h-11 text-sm sm:text-base touch-manipulation"
                disabled={loading || !allValid}
              >
                {loading ? (
                  <span className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 sm:h-5 sm:w-5 border-b-2 border-white mr-2"></div>
                    Réinitialisation en cours...
                  </span>
                ) : (
                  'Réinitialiser le Mot de Passe'
                )}
              </Button>

              <p className="text-center text-slate-400 text-xs sm:text-sm">
                Vous vous rappelez de votre mot de passe?{' '}
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="text-orange-400 hover:text-orange-300 underline"
                >
                  Se connecter
                </button>
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
